import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { createGesture, Gesture, modalController, toastController } from '@ionic/core';
import { Component, Event, EventEmitter, Fragment, h, Prop, State } from '@stencil/core';
import { FileDoc, PersonDoc, PhotoDoc, TagData } from 'common';
import * as exifr from 'exifr';
import createPanZoom, { PanZoom } from 'panzoom';
import { FileService } from 'src/global/service/FileService';
import { PeopleService } from 'src/global/service/PeopleService';

@Component({
  tag: 'concourse-gallery-modal',
  styleUrl: 'concourse-gallery-modal.css'
})
export class ConcourseGalleryModal {
  static async present(projectId: string, modalTitle: string, photos: FileDoc[], selectPhotoFooter: boolean = false): Promise<PhotoDoc | null> {
    const modal = await modalController.create({
      component: 'concourse-gallery-modal',
      componentProps: { projectId, modalTitle, photos, selectPhotoFooter },
      cssClass: 'concourse-modal modal-size-90',
    });
    return new Promise<PhotoDoc | null>(resolve => {
      modal.addEventListener('dismiss', () => {
        modal.dismiss();
        resolve(null);
      });
      modal.addEventListener('selectPhoto', (e: CustomEvent) => {
        modal.dismiss();
        resolve(e.detail);
      });
      modal.present();
    });
  }

  @Prop() projectId: string;
  @Prop() modalTitle: string;
  @Prop() photos: PhotoDoc[];
  @Prop() selectPhotoFooter: boolean;

  @State() photo: PhotoDoc;
  @State() photoLarge: string;
  @State() showExif: boolean;
  @Event() dismiss: EventEmitter;
  @Event() selectPhoto: EventEmitter<PhotoDoc>;

  private panzoomElement: HTMLElement;
  private panZoom: PanZoom;
  private exifData: any;
  private gesture: Gesture;
  private personDocs: PersonDoc[] = [];

  get isMobile(): boolean {
    return globalThis.isMobile;
  }

  async componentWillLoad() {
    this.setPhoto(this.photos[0]);
    this.personDocs = await PeopleService.getPersonDocs(this.projectId);
  }

  async setPhoto(photo: PhotoDoc) {
    this.photo = photo;
    this.photoLarge = null;
    this.exifData = null;
    this.loadPhotoUrl(photo);

    if (this.panzoomElement) {
      this.panZoom.moveTo(0, 0);
      this.panZoom.zoomAbs(0, 0, 1);
      if (this.gesture) {
        this.gesture.enable();
      }
    }
  }

  async componentDidLoad() {
    this.panZoom = createPanZoom(this.panzoomElement, {
      maxZoom: 100,
      minZoom: 0.05,
      zoomDoubleClickSpeed: 1,
      smoothScroll: false,
      bounds: true,
    });

    if (this.isMobile) {
      // Any zooming will disable the gesture so that users can
      // zoom right in on details without accidentally changing the photo
      this.panZoom.on('transform', () => {
        if (this.panZoom.getTransform().scale != 1) {
          this.gesture.enable(false);
        }
      });

      this.gesture = createGesture({
        el: this.panzoomElement,
        gestureName: 'photo-swipe',
        direction: 'x',
        threshold: 10,
        onEnd: ev => {
          const transX = this.panZoom.getTransform().x;
          const photoImg = this.panzoomElement.getElementsByClassName('photo-img')[0];
          const photoImgWidth = photoImg?.getBoundingClientRect().width || 100;
          // users must pan more than half the width of the photo to change the photo
          if (transX > photoImgWidth / 2) {
            this.viewPrev();
          } else if (transX < -photoImgWidth / 2) {
            this.viewNext();
          }
        },
      });
      this.gesture.enable();
    }
  }

  viewNext() {
    console.log('selectNext: ');
    const index = this.photos.indexOf(this.photo);
    if (index < this.photos.length - 1) {
      this.setPhoto(this.photos[index + 1]);
    } else {
      this.setPhoto(this.photos[0]);
    }
  }

  viewPrev() {
    console.log('selectPrev: ');
    const index = this.photos.indexOf(this.photo);
    if (index > 0) {
      this.setPhoto(this.photos[index - 1]);
    } else {
      this.setPhoto(this.photos[this.photos.length - 1]);
    }
  }

  zoomIn() {
    if (this.panZoom && this.panzoomElement) {
      const rect = this.panzoomElement.getBoundingClientRect();
      this.panZoom.smoothZoom(rect.left + rect.width / 2, rect.top + rect.height / 2, 1.3);
    }
  }

  zoomOut() {
    if (this.panZoom && this.panzoomElement) {
      const rect = this.panzoomElement.getBoundingClientRect();
      this.panZoom.smoothZoom(rect.left + rect.width / 2, rect.top + rect.height / 2, 1 / 1.3);
    }
  }

  async loadPhotoUrl(photo: PhotoDoc) {
    const large = await FileService.loadS3File(photo.s3_key);
    if (this.photo == photo) {
      this.photoLarge = large;
      this.exifData = await exifr.parse(large);
      console.log('exifr metadata:', this.exifData);
    }
  }

  async removeTag(tag: TagData) {
    this.photo.tags = this.photo.tags.filter(t => t.name != tag.name);
    this.photo = await FileService.savePhoto(this.projectId, this.photo);
  }

  async downloadImage() {
    const base64 = this.photoLarge ? this.photoLarge : this.photo.thumb;
    const name = this.photo.name || 'photo.jpg';
    if (Capacitor.isNativePlatform()) {
      try {
        const safeName = name.replace(/[/\\]/g, '_');
        const { uri } = await Filesystem.writeFile({
          path: safeName,
          data: base64,
          directory: Directory.Cache,
        });
        await Share.share({
          title: name,
          files: [uri],
          dialogTitle: 'Save image',
        });
      } catch (error) {
        console.error('Error downloading image:', error);
        const toast = await toastController.create({
          message: `Failed to download image: ${error.message}`,
          duration: 3000,
          position: 'bottom',
        });
        await toast.present();
      }
    } else {
      const link = document.createElement('a');
      link.href = `data:image/jpeg;base64,${base64}`;
      link.download = name;
      link.click();
    }
  }

  render() {
    const base64 = this.photoLarge ? this.photoLarge : this.photo.thumb;
    const ratio = this.photo.thumb_width / this.photo.thumb_height;
    const person = this.personDocs.find(p => p._id === this.photo.created_by);
    const formattedDate = `${new Date(this.photo.created_date).toLocaleString('default', { month: 'long' })} ${new Date(this.photo.created_date).getDate()} ${new Date(this.photo.created_date).getFullYear()}`;
    const title = this.isMobile ? "" : person ? formattedDate + ' by ' + PeopleService.getFullName(person) : formattedDate;
    return (
      <Fragment>
        <modal-header modalTitle={title} onDismiss={() => this.dismiss.emit()}>
          <ion-buttons>
            <ion-button fill="clear" size="small" onClick={() => this.zoomIn()}>
              <ion-icon slot="icon-only" name="add-circle-outline"></ion-icon>
            </ion-button>
            <ion-button fill="clear" size="small" onClick={() => this.zoomOut()}>
              <ion-icon slot="icon-only" name="remove-circle-outline"></ion-icon>
            </ion-button>
            <div class="toolbar-separator" />
            {this.photos.length > 1 && (
              <div class="photo-counter-controls">
                <ion-button fill="clear" size="small" data-testid="view-prev-button" onClick={() => this.viewPrev()}>
                  <ion-icon slot="icon-only" name="chevron-back"></ion-icon>
                </ion-button>
                <span class="photo-counter">
                  {this.photos.indexOf(this.photo) + 1}/{this.photos.length}
                </span>
                <ion-button fill="clear" size="small" data-testid="view-next-button" onClick={() => this.viewNext()}>
                  <ion-icon slot="icon-only" name="chevron-forward"></ion-icon>
                </ion-button>
              </div>
            )}
            {this.photos.length > 1 && <div class="toolbar-separator" />}
            <ion-button fill="clear" onclick={() => (this.showExif = !this.showExif)}>
              <ion-icon slot="icon-only" name="information-circle-outline"></ion-icon>
            </ion-button>
          </ion-buttons>
        </modal-header>
        <div>
          <div class="pan-zoom-wrapper" style={{ width: `100%`, height: `100%`, aspectRatio: `${ratio}` }}>
            <div class="pan-zoom" ref={el => (this.panzoomElement = el as HTMLElement)}>
              <img class="photo-img" src={`data:image/jpeg;base64,${base64}`} style={{ width: `100%`, aspectRatio: `${ratio}` }} />
            </div>
          </div>
          {!this.photoLarge && (
            <div class="center-content">
              <ion-spinner />
            </div>
          )}
          <div class={this.showExif ? 'photo-detail-text' : 'hidden'}>
            {this.showExif && (
              <div class="ion-display-flex ion-align-items-start">
                <div class="ion-display-md-flex ion-justify-content-between ion-flex-grow-1">
                  <div class="photo-data ion-margin-end">
                    File Name: {this.photo.name}
                    <br />
                    Description: {this.photo.description}
                    <br />
                    Dimensions: {this.photo.thumb_width} x {this.photo.thumb_height}
                    <br />
                    File Size: {FileService.getFileSize(this.photo.num_bytes)}
                    <br />
                    Created: {new Date(this.photo.created_date).toLocaleString()}
                    <br />
                    Tags:
                    {this.photo.tags?.length > 0 ? (
                      this.photo.tags.map((tag: TagData) => <concourse-tag projectId={this.projectId} data={tag} editable={true} onRemove={() => this.removeTag(tag)} />)
                    ) : (
                      <span>No tags</span>
                    )}
                  </div>
                  <div class="tags-container">
                    <person-chip person={person} textColor="#ffffff" />
                  </div>
                </div>
                <ion-button fill="clear" onClick={() => this.downloadImage()}>
                  <ion-icon slot="icon-only" name="cloud-download-outline"></ion-icon>
                </ion-button>
              </div>
            )}
          </div>
          {this.selectPhotoFooter && (
            <div class="sticky-bottom-right">
              <ion-button color="primary" onClick={() => this.selectPhoto.emit(this.photo)}>
                <ion-label>Select Photo</ion-label>
                <ion-icon name="checkmark-circle"></ion-icon>
              </ion-button>
            </div>
          )}
        </div>
      </Fragment>
    );
  }
}
